import { Avatar, Card, Divider, Descriptions, Tag, Row, Col, Space, Typography } from "antd";
import { ReactComponent as NameIcon } from "../../assets/profile/name.svg";
import { ReactComponent as PhoneIcon } from "../../assets/profile/phone.svg";
import { ReactComponent as EmailIcon } from "../../assets/profile/email.svg";
import { ReactComponent as TypeIcon } from "../../assets/profile/type.svg";
import { ReactComponent as CodeIcon } from "../../assets/profile/code.svg";
import { IdCard, Map, Settings, Star, LayoutDashboard, Key, Building } from "lucide-react";
import { Link } from "react-router-dom";
import useProfile from "./useProfile";
import useAuth from "../../hooks/useAuth";
import { USER_TYPES_TEXT } from "../../constants/constants";
import { Country } from "country-state-city";
import EditButton from "../../components/general/editButton/editButton";
import UserModal from "../../components/modals/user";
import ChangePasswordModal from "../../components/modals/changePassword";
import { useState } from "react";

const { Title, Text } = Typography;

function Profile() {
  const {
    form,
    handleCancel,
    handleEdit,
    handleFileChange,
    handleOk,
    loading,
    uploads,
    user,
    userModal,
    url,
  } = useProfile();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const { rolePermissions } = useAuth();
  
  let phoneStr = "No especificado";
  if (user?.phone) {
    let country = Country.getCountryByCode(user?.phonePrefix);
    const code = country?.phonecode ? (country.phonecode.includes("+") ? country.phonecode : `+${country.phonecode}`) : "";
    phoneStr = `${code} ${user.phone}`;
  }

  let identityStr = "No provisto";
  if (user?.identityType && user?.identityNumber) {
    identityStr = `${user.identityType}: ${user.identityNumber}`;
  } else if (user?.identityNumber) {
    identityStr = user.identityNumber;
  }

  const getRoleTagColor = (type: string) => {
    switch(type) {
      case "admin": 
      case "super_admin": return "red";
      case "empresa": return "blue";
      case "gestor": return "purple";
      case "empleado": return "green";
      default: return "default";
    }
  };

  const isAdminOrGestor = user?.type === "admin" || user?.type === "gestor";

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", paddingBottom: 40 }}>
      <Card 
        bodyStyle={{ padding: 0 }} 
        style={{ borderRadius: 12, overflow: "hidden", marginBottom: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
      >
        {/* Cover Header */}
        <div style={{ 
          height: 160, 
          background: "linear-gradient(135deg, #1e293b 0%, #81bc3d 100%)",
          position: "relative"
        }}>
           <div style={{ position: "absolute", top: 16, right: 16 }}>
             <EditButton record={user} handleEdit={handleEdit} />
           </div>
        </div>

        {/* Profile Info Container */}
        <div style={{ padding: "0 32px 32px 32px" }}>
          
          {/* Avatar overlapped */}
          <div style={{ display: "flex", gap: 24, marginTop: -60, alignItems: "flex-end", marginBottom: 24 }}>
            <Avatar
              size={140}
              style={{
                border: "4px solid white",
                backgroundColor: "#f1f5f9",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                color: "#94a3b8",
                fontSize: 48
              }}
              icon={url ? <img src={url} alt={user?.name[0]} style={{ objectFit: 'cover' }} /> : undefined}
            >
              {!url && user?.name?.[0]?.toUpperCase()}
            </Avatar>
            <div style={{ paddingBottom: 10 }}>
              <Title level={2} style={{ margin: 0, color: "#0f172a" }}>{user?.name}</Title>
              <Space style={{ marginTop: 8 }}>
                <Tag color={getRoleTagColor(user?.type)} style={{ fontSize: 13, padding: "2px 10px", borderRadius: 12 }}>
                  {USER_TYPES_TEXT[user?.type]}
                </Tag>
                <Text type="secondary" style={{ fontSize: 13 }}>ID Interno: {user?.code}</Text>
              </Space>
            </div>
          </div>

          <Divider />

          {/* User Details Grid */}
          <Title level={5} style={{ fontSize: 18, marginBottom: 16, marginTop: 0 }}>Información de Contacto</Title>
          <Row gutter={[16, 16]}>
            {[
              { label: "Nombre completo", icon: <NameIcon width={18} />, value: user?.name },
              { label: "Correo Electrónico", icon: <EmailIcon width={18} />, value: <Text copyable style={{ margin: 0 }}>{user?.email}</Text> },
              { label: "Teléfono", icon: <PhoneIcon width={18} />, value: user?.phone ? phoneStr : null },
              { label: "Rol del Usuario", icon: <TypeIcon width={18} />, value: USER_TYPES_TEXT[user?.type] },
              { label: "Código de Sistema", icon: <CodeIcon width={18} />, value: user?.code },
              { label: "Identificación", icon: <IdCard size={18} color="#81bc3d" />, value: user?.identityNumber ? identityStr : null },
              ...(!isAdminOrGestor ? [{ label: "Empresa", icon: <Building size={18} color="#81bc3d" />, value: user?.companyName }] : [])
            ].map((item, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <div style={{ display: "flex", alignItems: "center", padding: "16px", background: "#ffffff", border: "1px solid #f0f0f0", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#f0fbe4", display: "flex", justifyContent: "center", alignItems: "center", marginRight: 16, flexShrink: 0 }}>
                    {item.icon}
                  </div>
                  <div style={{ overflow: "hidden", width: "100%" }}>
                    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#262626", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.value || <span style={{ color: "#bfbfbf", fontWeight: "normal", fontStyle: "italic" }}>No asignado</span>}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Card>

      {/* Quick Links Section */}
      <Title level={4} style={{ marginBottom: 16 }}>Accesos Rápidos</Title>
      <Row gutter={[16, 16]}>
        {isAdminOrGestor && (
          <Col xs={24} sm={12} md={6}>
            <Link to="/admin/dashboard">
              <Card hoverable style={{ height: "100%", borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ padding: 12, borderRadius: "50%", background: "#f1f5f9", color: "#1e293b" }}>
                    <LayoutDashboard size={24} />
                  </div>
                  <Text strong>Panel de Control</Text>
                </div>
              </Card>
            </Link>
          </Col>
        )}
        {(rolePermissions?.includes("ui:map") || isAdminOrGestor) && (
          <Col xs={24} sm={12} md={6}>
            <Link to="/admin/map/1">
              <Card hoverable style={{ height: "100%", borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ padding: 12, borderRadius: "50%", background: "#e6f4ff", color: "#1677ff" }}>
                    <Map size={24} />
                  </div>
                  <Text strong>Mapa Acústico</Text>
                </div>
              </Card>
            </Link>
          </Col>
        )}
        <Col xs={24} sm={12} md={6}>
          <Link to={isAdminOrGestor ? "/admin/subscription-plans" : "/admin/subscriptions"}>
            <Card hoverable style={{ height: "100%", borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ padding: 12, borderRadius: "50%", background: "#f9f0ff", color: "#722ed1" }}>
                  <Star size={24} />
                </div>
                <Text strong>{isAdminOrGestor ? "Gestión de Planes" : "Mis Suscripciones"}</Text>
              </div>
            </Card>
          </Link>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div onClick={() => setIsPasswordModalOpen(true)}>
            <Card hoverable style={{ height: "100%", borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ padding: 12, borderRadius: "50%", background: "#fffbe6", color: "#d48806" }}>
                  <Key size={24} />
                </div>
                <Text strong>Cambiar Contraseña</Text>
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      <UserModal
        editing={true}
        form={form}
        isModalOpen={userModal.isModalOpen}
        handleCancel={handleCancel}
        handleFileChange={handleFileChange}
        handleOk={handleOk}
        loading={loading}
        uploads={uploads}
        url={url}
      />

      <ChangePasswordModal 
        open={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)} 
      />
    </div>
  );
}

export default Profile;
